import { useRef } from 'react'

import { Box, Button, Link, Section, Text } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import {
  EmployeeBlocksEmployeeFragment,
  EmployeeBlocksEmployeeFragmentDoc,
  EmployeeBlocksUpdateEmployeeIrpfDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import FormComponent from 'modules/core/components/Form'
import FormRow from 'modules/core/components/FormRow'
import DeprecatedInputText from 'modules/core/components/Inputs/Text'
import { useMutation } from 'modules/core/hooks/useMutation'
import buildUrl from 'modules/core/lib/buildUrl'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface Props {
  employee: EmployeeBlocksEmployeeFragment
  isCurrent: boolean
}

class IrpfForm extends Form {
  constructor(values: Record<string, unknown>) {
    super(values, {
      irpf_in_cents: 'cents',
    })
  }
}

const Irpf = ({ employee, isCurrent }: Props) => {
  // This block is both reading and writing the es_employee_metadata table. Thus, it wont make sense to show it if the employee is not spanish
  const [updatePersonalSituationData] = useMutation(EmployeeBlocksUpdateEmployeeIrpfDocument, {
    extractorFn: (data) => data.employeesMutations.updateEmployeesEmployee,
    update: (cache, result) => {
      const updatedEmployee = result.data?.employeesMutations?.updateEmployeesEmployee?.employee
      if (updatedEmployee) {
        cache.writeFragment({
          id: cache.identify(employee),
          fragment: EmployeeBlocksEmployeeFragmentDoc,
          fragmentName: 'EmployeeBlocksEmployee',
          data: {
            ...employee,
            esIrpfCents: updatedEmployee.esIrpfCents,
          },
        })
      }
    },
  })
  const form = useRef(
    new IrpfForm({
      irpf_in_cents: employee.esIrpfCents,
    })
  )
  const irpfForm = form.current

  const onSubmit = async () => {
    const irpfInCents = Math.floor(parseFloat(irpfForm.get('irpf_in_cents').value) * 100)
    const result = await updatePersonalSituationData({
      variables: {
        employeeId: employee.id,
        irpfInCents: irpfInCents,
      },
    })

    const handled = handleResult(result, (errors) => {
      if (errors) {
        const errorKeys = Object.keys(errors)
        const message =
          errorKeys.length > 0
            ? errorKeys
                .map((key) => {
                  return errors[key]
                })
                .join(', ')
            : i18n.t('profile.employee.irpf.update_irpf_data_error')
        app.setFlash({
          type: 'wrong',
          message: message,
        })
      }
    })

    if (handled.success) {
      irpfForm.cleanAll()
      app.setFlash({
        type: 'correct',
        message: i18n.t('profile.employee.irpf.update_irpf_data'),
      })
    }

    return handled
  }

  return (
    <Section
      description={i18n.tCurrent('hiring.irpf_description', isCurrent)}
      illustration="control-navigation-adjust-sound"
      title={i18n.t('common.irpf')}
    >
      <Box
        gap="s24"
        background="grey200"
        padding="s32"
        width="s360"
        alignSelf="flexEnd"
        borderRadius={{
          all: 'abs004',
        }}
      >
        <FormComponent>
          <FormRow>
            <DeprecatedInputText
              label={i18n.t('employee_form.irpf.label')}
              field={irpfForm.get('irpf_in_cents')}
            />
          </FormRow>
          {irpfForm.isDirty && (
            <>
              <FormRow>
                <Text.Normal>{i18n.t('employee_form.irpf.information')}</Text.Normal>
              </FormRow>
              <FormRow>
                <Link.Href target="_blank" href={buildUrl('/solicitud_irpf_superior.pdf')}>
                  {i18n.t('employee_form.irpf.download_irpf_document')}
                </Link.Href>
              </FormRow>
              <FormRow footer>
                <Button onClick={onSubmit}>{i18n.t('common.submit')}</Button>
              </FormRow>
            </>
          )}
        </FormComponent>
      </Box>
    </Section>
  )
}

export default observer(Irpf)
